// src/context/UserTierContext.js
import React, { createContext, useContext, useMemo } from "react";
import { isAdmin } from "../utils/adminUtils";

/**
 * Free tier rules:
 *   - All Easy challenges unlocked for everyone (guests + free)
 *   - 1 Medium challenge per topic unlocked for guests + free
 *   - Apex30 fully free for everyone
 *   - 2 flashcard decks free (controlled in DB via is_free flag)
 *   - Hard challenges + solutions require Pro/Team/Admin
 */

const UserTierContext = createContext({
  tier: "guest",
  isAdmin: false,
  canAccessChallenge: () => false,
  canViewSolution: () => false,
  canAccessApex30: () => true,
  canAccessMockExam: () => false,
});

export function UserTierProvider({ user, subscription, children }) {
  const value = useMemo(() => {
    const admin   = isAdmin(user);
    const loggedIn = !!user;

    let tier = "guest";
    if (admin)                  tier = "admin";
    else if (subscription === "team") tier = "team";
    else if (subscription === "pro")  tier = "pro";
    else if (loggedIn)          tier = "free";

    const isPaid = tier === "pro" || tier === "team" || tier === "admin";

    return {
      tier,
      isAdmin: admin,

      /**
       * @param {object} challenge — full challenge object with difficulty, topic, order_index
       * @param {Set} solvedTopicMediums — Set of topics where the user already used their free Medium
       */
      canAccessChallenge: (challenge, solvedTopicMediums = new Set()) => {
        if (isPaid) return true;
        if (!challenge) return false;

        // All Easy challenges free for everyone
        if (challenge.difficulty === "Easy") return true;

        // Hard always requires paid
        if (challenge.difficulty === "Hard") return false;

        // Medium: 1 per topic free (the first Medium in that topic by order_index)
        if (challenge.difficulty === "Medium") {
          // If this topic's free Medium slot is not yet used, allow it
          return !solvedTopicMediums.has(challenge.topic);
        }

        return false;
      },

      // Solutions require paid
      canViewSolution: () => isPaid,

      // Apex30 is free for everyone
      canAccessApex30: () => true,

      // Mock exam requires paid
      canAccessMockExam: () => isPaid,
    };
  }, [user, subscription]);

  return (
    <UserTierContext.Provider value={value}>
      {children}
    </UserTierContext.Provider>
  );
}

export function useUserTier() {
  return useContext(UserTierContext);
}